/* eslint-disable import/no-unresolved */
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import * as Yup from 'yup';
import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { useTravel } from 'v3/pages/Travel';
import { fetchReceiverTypes } from 'utils/fetches';
import {
  CPF_MASK_ALPHANUMERIC,
  CPF_DIGITS_LENGTH,
  CNPJ_MASK,
  CNPJ_DIGITS_LENGTH,
  CPF_CNPJ_REGEX,
} from 'v3/utils/masks';

import { onlyDigits } from 'v3/utils/functions';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import Select from 'v3/components/Select';
import Upload from 'v3/components/Upload';
import Button from 'v3/components/Button';
import CheckBox from 'components/CheckBox';
import Input from 'v3/components/Input';

const FILE_TYPE_OPTIONS = [
  { label: 'Canhoto', value: 'canhoto' },
  { label: 'Outros', value: 'others' },
];

const applyMask = value => {
  const alphaLen = onlyDigits(value).length;
  if (alphaLen <= CPF_DIGITS_LENGTH) {
    return CPF_MASK_ALPHANUMERIC;
  }

  return CNPJ_MASK;
};

function buildSchema(sendOccurrence) {
  return Yup.object().shape({
    fileType: Yup.object().required('Selecione o tipo do documento'),
    file: Yup.mixed().required('Selecione um arquivo'),
    ...(sendOccurrence && {
      nameReceiver: Yup.string().required('Nome do recebedor obrigatório'),
      typeReceiver: Yup.object()
        .nullable()
        .required('Tipo do recebedor obrigatório'),
      documentReceiver: Yup.string()
        .required('Documento do recebedor obrigatório')
        .matches(CPF_CNPJ_REGEX, 'Documento inválido'),
    }),
  });
}

export default function UploadModal({
  isOpen,
  onClose,
  emittedId,
  docType,
  fetchDocuments,
  nfs = [],
}) {
  const [fileType, setFileType] = useState();
  const [file, setFile] = useState();
  const [selectedNfs, setSelectedNfs] = useState([]);
  const [sendOccurrence, setSendOccurrence] = useState(false);
  const [nameReceiver, setNameReceiver] = useState('');
  const [typeReceiver, setTypeReceiver] = useState(null);
  const [documentReceiver, setDocumentReceiver] = useState('');
  const [description, setDescription] = useState('');
  const [typeReceiverOptions, setTypeReceiverOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isInFlight, setInFlight] = useState(false);
  const snackbar = useSnackbar();
  const travel = useTravel();

  useEffect(() => {
    if (isOpen) {
      fetchReceiverTypes().then(setTypeReceiverOptions);
    }
  }, [isOpen]);

  const nfOptions = useMemo(
    () => (nfs || []).map(nf => ({ label: nf.nfNumber, value: nf.id })),
    [nfs]
  );

  function buildUploadFormData() {
    const data = new FormData();
    data.append('document_file', file[0]);
    data.append('dataType', fileType.value);

    selectedNfs.forEach(nf => {
      data.append('nfIds[]', nf.value);
    });

    data.append('createOccurrence', String(sendOccurrence));

    if (sendOccurrence) {
      data.append('nameReceiver', nameReceiver);
      data.append('documentReceiver', documentReceiver);
      data.append('typeReceiverId', typeReceiver.id);
      if (description) {
        data.append('description', description);
      }
    }

    return data;
  }

  function resetForm() {
    setFileType(null);
    setFile(null);
    setSelectedNfs([]);
    setSendOccurrence(false);
    setNameReceiver('');
    setTypeReceiver(null);
    setDocumentReceiver('');
    setDescription('');
    setErrors({});
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  async function finalizeUpload(data) {
    await cooperplaceApi.post(`travel-attachments/${emittedId}`, data);

    resetForm();
    travel.retrieveOccurrences();
    fetchDocuments(emittedId, { refetch: true, setLoading: setInFlight });
    onClose();
  }

  async function uploadFile() {
    try {
      setInFlight(true);
      await buildSchema(sendOccurrence).validate(
        {
          fileType,
          file: file?.[0],
          nameReceiver,
          typeReceiver,
          documentReceiver,
        },
        { abortEarly: false }
      );

      await finalizeUpload(buildUploadFormData());
    } catch (ex) {
      if (ex instanceof Yup.ValidationError) {
        setErrors(
          ex.inner.reduce(
            (acc, error) => ({ ...acc, [error.path]: error.message }),
            {}
          )
        );
      } else {
        const message =
          ex?.response?.data?.message || 'Ocorreu um erro ao enviar o arquivo';
        snackbar.show(<Text>{message}</Text>, { type: 'error' });
      }
    } finally {
      setInFlight(false);
    }
  }

  const fileTypes = useMemo(() => {
    const options = [...FILE_TYPE_OPTIONS];
    if (docType === 'CTE') {
      options.push({ label: 'CT-e', value: 'cte' });
    } else if (docType === 'RPS') {
      options.push({ label: 'RPS', value: 'rps' });
    }
    return options;
  }, [docType]);

  return (
    <Modal
      show={isOpen}
      handleClose={handleClose}
      heading={
        <Text color="dark" type="header" weight="500">
          Enviar documentos
        </Text>
      }
      body={
        <>
          <Select
            label="Tipo de arquivo *"
            options={fileTypes}
            onChange={value => {
              setFileType(value);
              setSelectedNfs([]);
              setSendOccurrence(false);
            }}
            value={fileType}
            error={errors.fileType}
          />
          {fileType?.value === 'canhoto' && nfOptions.length > 0 && (
            <div className="my-3">
              <Select
                label="Notas fiscais"
                options={nfOptions}
                onChange={setSelectedNfs}
                value={selectedNfs}
                multiple
                placeholder="Selecione as NFs..."
              />
            </div>
          )}
          <div className="my-3 text-center">
            <Text color="dark" type="regular">
              As extensões de arquivos aceitas são .jpg .png .pdf
            </Text>
          </div>
          <Upload
            name="file"
            label="Arquivo máximo de 5MB"
            accept=".jpg,.jpeg,.png,.pdf"
            value={file}
            onChange={({ target }) => setFile(target.files)}
          />
          {!!errors.file && (
            <Text color="error" type="little">
              {errors.file}
            </Text>
          )}
          {fileType?.value === 'canhoto' && (
            <div className="mt-3">
              <CheckBox
                label="Enviar ocorrência"
                labelStyle={{ textTransform: 'capitalize', color: '#464E5F' }}
                value={sendOccurrence}
                onChange={() => setSendOccurrence(prev => !prev)}
              />
            </div>
          )}
          {sendOccurrence && (
            <>
              <div className="mt-3">
                <Select
                  label="Tipo recebedor *"
                  options={typeReceiverOptions}
                  value={typeReceiver}
                  onChange={setTypeReceiver}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  error={errors.typeReceiver}
                />
              </div>
              <Row className="mt-3">
                <Col md={6}>
                  <Input
                    label="Nome recebedor *"
                    value={nameReceiver}
                    onChange={({ target }) => setNameReceiver(target.value)}
                    error={errors.nameReceiver}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Documento recebedor (CPF/CNPJ) *"
                    placeholder="Adicionar CPF/CNPJ do recebedor"
                    value={documentReceiver}
                    onChange={({ target }) => {
                      if (onlyDigits(target.value).length > CNPJ_DIGITS_LENGTH)
                        return;
                      setDocumentReceiver(target.value);
                    }}
                    error={errors.documentReceiver}
                    masked
                    mask={applyMask}
                  />
                </Col>
              </Row>
              <div className="mt-3">
                <Input
                  label="Descrição"
                  value={description}
                  onChange={({ target }) => setDescription(target.value)}
                />
              </div>
            </>
          )}
          <div className="d-flex flex-column align-items-center mt-4">
            <Button onClick={uploadFile} loading={isInFlight}>
              <Text type="regular" weight="500">
                Enviar
              </Text>
            </Button>
          </div>
        </>
      }
    />
  );
}
